import { useRef, useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play, RotateCcw } from "lucide-react";

export function DreamRunner() {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | run | over
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("rea_runner_best") || 0));
  const g = useRef(null);
  const statusRef = useRef("idle");
  statusRef.current = status;

  const H = 260;

  const resetGame = useCallback((w) => {
    g.current = {
      W: w, groundY: H - 42, player: { x: 60, y: H - 42 - 36, vy: 0, size: 36, jumping: false },
      obstacles: [], speed: 6, spawn: 40, score: 0, frame: 0,
    };
  }, []);

  const jump = useCallback(() => {
    const s = statusRef.current;
    if (s === "idle") { setStatus("run"); statusRef.current = "run"; return; }
    if (s === "over") {
      const c = canvasRef.current; resetGame(c.width); setScore(0); setStatus("run"); statusRef.current = "run"; return;
    }
    const st = g.current;
    if (st && !st.player.jumping) { st.player.vy = -13.5; st.player.jumping = true; }
  }, [resetGame]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const setSize = () => {
      const w = canvas.parentElement.getBoundingClientRect().width;
      canvas.width = w; canvas.height = H;
      if (g.current) g.current.W = w; else resetGame(w);
    };
    setSize();
    window.addEventListener("resize", setSize);

    let raf;
    const draw = () => {
      const st = g.current;
      ctx.clearRect(0, 0, st.W, H);
      // ground
      ctx.strokeStyle = "#141414"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(0, st.groundY); ctx.lineTo(st.W, st.groundY); ctx.stroke();
      // dotted texture
      ctx.fillStyle = "#c9c5bd";
      for (let x = (st.frame * (statusRef.current === "run" ? st.speed : 0)) % 40; x < st.W; x += 40) ctx.fillRect(x, st.groundY + 10, 6, 2);

      const running = statusRef.current === "run";
      if (running) {
        st.frame++;
        // physics
        st.player.vy += 0.72; st.player.y += st.player.vy;
        if (st.player.y >= st.groundY - st.player.size) { st.player.y = st.groundY - st.player.size; st.player.vy = 0; st.player.jumping = false; }
        // spawn
        st.spawn--;
        if (st.spawn <= 0) {
          const h = 26 + Math.random() * 34;
          st.obstacles.push({ x: st.W + 10, w: 16 + Math.random() * 16, h });
          st.spawn = Math.max(38, 90 - st.speed * 4) + Math.random() * 40;
        }
        st.obstacles.forEach((o) => (o.x -= st.speed));
        st.obstacles = st.obstacles.filter((o) => o.x + o.w > -10);
        st.score += 1;
        st.speed += 0.0025;
        if (st.score % 3 === 0) setScore(Math.floor(st.score / 3));
        // collision
        const p = st.player;
        for (const o of st.obstacles) {
          if (p.x + p.size - 6 > o.x + 3 && p.x + 6 < o.x + o.w - 3 && p.y + p.size - 4 > st.groundY - o.h) {
            statusRef.current = "over"; setStatus("over");
            const fs = Math.floor(st.score / 3);
            setBest((b) => { const nb = Math.max(b, fs); localStorage.setItem("rea_runner_best", String(nb)); return nb; });
            break;
          }
        }
      }
      // draw obstacles (dark hurdles)
      ctx.fillStyle = "#141414";
      st.obstacles.forEach((o) => ctx.fillRect(o.x, st.groundY - o.h, o.w, o.h));
      // draw player
      const p = st.player;
      ctx.fillStyle = "#141414";
      ctx.fillRect(p.x, p.y, p.size, p.size);
      // little "roof" to hint a home/buyer
      ctx.beginPath(); ctx.moveTo(p.x - 3, p.y); ctx.lineTo(p.x + p.size / 2, p.y - 12); ctx.lineTo(p.x + p.size + 3, p.y); ctx.closePath(); ctx.fill();
      // score on canvas
      ctx.fillStyle = "#141414"; ctx.font = "600 14px 'JetBrains Mono', monospace";
      ctx.textAlign = "right";
      ctx.fillText(`${Math.floor(st.score / 3)}`.padStart(5, "0"), st.W - 12, 26);

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", setSize); };
  }, [resetGame]);

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space" || e.code === "ArrowUp") { e.preventDefault(); jump(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [jump]);

  return (
    <div data-testid="dream-runner">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-display text-2xl font-extrabold uppercase text-foreground">Dream Run</h4>
          <p className="font-mono text-xs text-muted-foreground">Jump the hurdles on the way to your dream home. Space / tap to jump.</p>
        </div>
        <div className="border border-border bg-background px-4 py-2 text-center">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Best</p>
          <p className="font-mono text-xl font-bold text-foreground">{String(best).padStart(5, "0")}</p>
        </div>
      </div>
      <div className="relative border border-border bg-card" onClick={jump}>
        <canvas ref={canvasRef} className="block w-full cursor-pointer touch-none" onTouchStart={(e) => { e.preventDefault(); jump(); }} data-testid="runner-canvas" />
        {status !== "run" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-background/85 backdrop-blur-sm" data-testid="runner-overlay">
            <p className="font-display text-4xl font-black uppercase text-foreground">{status === "over" ? "Ouch! Hurdle hit." : "Dream Run"}</p>
            {status === "over" && <p className="font-mono text-sm text-muted-foreground">Score {score} · Best {best}</p>}
            <Button onClick={jump} className="rounded-none bg-cyan font-mono text-xs uppercase tracking-widest text-white hover:bg-cyan/90" data-testid="runner-start">
              {status === "over" ? <><RotateCcw className="mr-2 h-4 w-4" /> Run Again</> : <><Play className="mr-2 h-4 w-4" /> Start Running</>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
